/**
 * Casework (design section 11, node kgcp3; alderperson offices, feature `casework`): the inbox of the office's cases
 * with their 311 requests, owners and age, and the selected case's timeline and block pattern. Paints from
 * snapshot:casework (worker/src/casework.js caseworkSnapshot); refetches when the team's OrgRoom says `casework.changed`.
 * Not in the design, kept minimal: the New case / Import from 311 / owner pickers and the pattern's request list.
 */
import { Dropdown } from "../components/shared/Dropdown";
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight, Check, Download, Mail, MessageSquare, Plus, RefreshCw, Reply, Send, X, CircleDot } from "lucide-react";
import { TabBar } from "../components/shared/ui";
import { List, ListRow, RowCell, RowMain, RowSub, RowTitle, RowTrail } from "../components/shared/List";
import { toastStore } from "../stores/toastStore";
import { errorText } from "../lib/apiError";
import { useBusy } from "../hooks/useBusy";
import { useFocusTrap } from "../hooks/useFocusTrap";
import {
  ageTone, casesFor, eventTime, firstName, initialsOf, patternLine, STATUS_LABEL,
  type CaseDetail, type CaseEvent, type CaseRow, type CaseTab, type CaseworkSnapshot, type Member, type ServiceRequest,
} from "./model";
import "./casework.css";

const API = "/api/dashboard/casework";
const inlined = <T,>(id: string): T | null => {
  const el = typeof document !== "undefined" ? document.getElementById(id) : null;
  try { return el?.textContent ? (JSON.parse(el.textContent) as T) : null; } catch { return null; }
};
async function call<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
  const r = await fetch(`${API}${path}`, { method: init.method ?? "GET", credentials: "include",
    headers: init.body === undefined ? undefined : { "content-type": "application/json" }, body: init.body === undefined ? undefined : JSON.stringify(init.body) });
  const d = await r.json().catch(() => null);
  if (!r.ok) throw new Error(errorText(d) || "Something went wrong");
  return d as T;
}
const failed = (e: unknown) => toastStore.getState().show({ message: (e as Error).message || "Something went wrong" });

const EVENT_ICON: Record<CaseEvent["kind"], typeof Mail> = { email: Mail, filed: Send, city: RefreshCw, reply: MessageSquare, note: CircleDot, created: Plus, closed: Check, reopened: RefreshCw };

export function CaseworkPage() {
  const [snap, setSnap] = useState<CaseworkSnapshot | null>(() => inlined<CaseworkSnapshot>("snapshot:casework"));
  const [tab, setTab] = useState<CaseTab>("open");
  const [selected, setSelected] = useState<CaseDetail | null>(() => snap?.selected ?? null);
  const [dialog, setDialog] = useState<"new" | "import" | "pattern" | null>(null);
  const selectedId = useRef<string | null>(selected?.id ?? null);
  selectedId.current = selected?.id ?? null;

  const reload = async () => {
    const s = await call<CaseworkSnapshot>("").catch(() => null);
    if (!s) return;
    setSnap(s);
    const id = selectedId.current;
    if (id) { const d = await call<{ case: CaseDetail }>(`/cases/${encodeURIComponent(id)}`).catch(() => null); if (d) setSelected(d.case); }
  };
  useEffect(() => {
    if (!snap) void reload();
    const on = (e: Event) => { if (String((e as CustomEvent<{ t?: string }>).detail?.t).startsWith("casework.")) void reload(); };
    addEventListener("team:work", on);
    return () => removeEventListener("team:work", on);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async (row: CaseRow) => {
    setSelected((cur) => (cur?.id === row.id ? cur : { ...row, notes: null, events: [], request: null, pattern: null }));
    const d = await call<{ case: CaseDetail }>(`/cases/${encodeURIComponent(row.id)}`).catch(() => null);
    if (d && selectedId.current === row.id) setSelected(d.case);
  };
  const apply = (d: CaseDetail) => { setSelected(d); void reload(); };

  if (!snap) return null;
  const { stats } = snap;
  const counts = { open: casesFor("open", snap.cases, snap.me).length, waiting: casesFor("waiting", snap.cases, snap.me).length, mine: casesFor("mine", snap.cases, snap.me).length };
  const rows = casesFor(tab, snap.cases, snap.me);
  const member = (id: string | null) => snap.members.find((m) => m.id === id) ?? null;

  return (
    <div className="cw">
      <header className="cw-head">
        <h1 className="cw-title">Casework</h1>
        <div className="cw-actions">
          <button className="cw-btn" onClick={() => setDialog("import")}><Download size={14} aria-hidden />Import from 311</button>
          <button className="cw-btn cw-btn--primary" onClick={() => setDialog("new")}><Plus size={14} aria-hidden />New case</button>
        </div>
      </header>
      <div className="cw-tiles">
        <Tile label="Open" value={stats.open} sub={stats.dueThisWeek ? `${stats.dueThisWeek} due this week` : null} />
        <Tile label="Waiting on the City" value={stats.waiting} />
        <Tile label="Median time to close" value={stats.medianDaysToClose === null ? "—" : `${stats.medianDaysToClose} ${stats.medianDaysToClose === 1 ? "day" : "days"}`}
          sub={stats.previous && stats.medianDaysToClose !== null && stats.previous.days > stats.medianDaysToClose ? `Down from ${stats.previous.days} in ${stats.previous.month}` : null} />
        <Tile label="Closed this month" value={stats.closedThisMonth} />
      </div>
      <TabBar label="Cases" active={tab} onChange={setTab} tabs={[
        { id: "open", label: `Open · ${counts.open}` }, { id: "waiting", label: `Waiting on the City · ${counts.waiting}` },
        { id: "mine", label: `Assigned to me · ${counts.mine}` }, { id: "closed", label: "Closed" }, { id: "all", label: "All" }]} />
      <div className="cw-cols">
        <div className="cw-table" role="table" aria-label="Cases">
          <List as="div" className="cw-list">
            <ListRow as="div" role="row" className="cw-row cw-row--head ls-head">
              <RowMain min={120}><span role="columnheader">Case</span></RowMain>
              <RowCell width={130} hide="phone"><span role="columnheader">Category</span></RowCell>
              <RowCell width={92} hide="phone"><span role="columnheader">311</span></RowCell>
              <RowCell width={56}><span role="columnheader">Owner</span></RowCell>
              <RowCell width={64}><span role="columnheader">Age</span></RowCell>
            </ListRow>
            {rows.map((c) => (
              <CaseLine key={c.id} c={c} selected={selected?.id === c.id} owner={member(c.ownerId)} members={snap.members} onOpen={() => void open(c)}
                onOwner={(ownerId) => void call<{ case: CaseDetail }>(`/cases/${encodeURIComponent(c.id)}`, { method: "PATCH", body: { ownerId } }).then((d) => { if (selected?.id === c.id) setSelected(d.case); void reload(); }).catch(failed)} />
            ))}
          </List>
        </div>
        {selected && <Side c={selected} onChange={apply} onPattern={() => setDialog("pattern")} />}
      </div>
      {dialog === "new" && <NewCase members={snap.members} me={snap.me} onClose={() => setDialog(null)} onDone={(d) => { setDialog(null); apply(d); }} />}
      {dialog === "import" && <ImportDialog onClose={() => setDialog(null)} onDone={(d) => { setDialog(null); apply(d); }} />}
      {dialog === "pattern" && selected?.pattern && <PatternDialog c={selected} onClose={() => setDialog(null)} />}
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string | number; sub?: string | null }) {
  return (
    <div className="cw-tile">
      <span className="cw-tile__l">{label}</span>
      <span className="cw-tile__v">{typeof value === "number" ? value.toLocaleString("en-US") : value}</span>
      {sub && <span className="cw-tile__s">{sub}</span>}
    </div>
  );
}

function CaseLine({ c, selected, owner, members, onOpen, onOwner }: {
  c: CaseRow; selected: boolean; owner: Member | null; members: Member[]; onOpen: () => void; onOwner: (id: string) => void;
}) {
  const [picking, setPicking] = useState(false);
  const tone = ageTone(c);
  return (
    <ListRow as="div" role="row" className="cw-row" current={selected} selected={selected} onOpen={onOpen}>
      <RowMain min={120}>
        <span className="cw-cell" role="cell">
          <RowTitle className="cw-case__t">{c.title}</RowTitle>
          <RowSub className="cw-case__s">{[c.name, c.block].filter(Boolean).join(" · ")}</RowSub>
        </span>
      </RowMain>
      <RowCell width={130} hide="phone" className="cw-c-cat"><span role="cell">{c.category && <span className="cw-chip">{c.category}</span>}</span></RowCell>
      <RowCell width={92} hide="phone" className="cw-c-sr"><span className="cw-mono" role="cell" data-empty={!c.srNumber || undefined}>{c.srNumber ?? "—"}</span></RowCell>
      <RowCell width={56} className="cw-c-owner"><span role="cell">
        <button className="cw-avatar" aria-label={owner?.name ?? "Owner"} title={owner?.name} aria-haspopup="listbox" aria-expanded={picking} onClick={() => setPicking((v) => !v)}>{initialsOf(owner?.name)}</button>
        {picking && (
          <span className="cw-menu" role="listbox" aria-label="Owner">
            {members.map((m) => (
              <button key={m.id} role="option" aria-selected={m.id === c.ownerId} onClick={() => { setPicking(false); if (m.id !== c.ownerId) onOwner(m.id); }}>
                <span className="cw-avatar" aria-hidden>{initialsOf(m.name)}</span>{m.name}
              </button>
            ))}
          </span>
        )}
      </span></RowCell>
      <RowCell width={64}><span className="cw-c-age" role="cell" data-tone={tone ?? undefined}>{c.ageDays} d</span></RowCell>
    </ListRow>
  );
}

function Side({ c, onChange, onPattern }: { c: CaseDetail; onChange: (d: CaseDetail) => void; onPattern: () => void }) {
  const { busy, run } = useBusy();
  const patch = (body: object) => run(async () => { try { onChange((await call<{ case: CaseDetail }>(`/cases/${encodeURIComponent(c.id)}`, { method: "PATCH", body })).case); } catch (e) { failed(e); } });
  const replied = () => void call<{ case: CaseDetail }>(`/cases/${encodeURIComponent(c.id)}/events`, { method: "POST", body: { kind: "reply", text: `Replied to ${firstName(c.name)}` } })
    .then((d) => onChange(d.case)).catch(() => {});
  const first = firstName(c.name);
  return (
    <aside className="cw-side" aria-label={c.title}>
      <div className="cw-side__head">
        <span className="cw-status" data-status={c.status}>{STATUS_LABEL[c.status]}</span>
        <h2 className="cw-side__title">{c.title}</h2>
        <span className="cw-side__who">{[c.name, c.email, c.phone].filter(Boolean).join(" · ")}</span>
      </div>
      {c.request && (
        <div className="cw-req">
          <div className="cw-req__top"><span className="cw-req__l">311 request</span><span className="cw-mono cw-req__sr">{c.request.srNumber}</span></div>
          <span className="cw-req__status">{[c.request.status, c.request.department].filter(Boolean).join(" · ")}</span>
        </div>
      )}
      {c.events.length > 0 && <>
        <span className="cw-label">TIMELINE</span>
        <ol className="cw-timeline">
          {c.events.map((e) => {
            const I = EVENT_ICON[e.kind] ?? CircleDot;
            return (
              <li key={e.id}>
                <span className="cw-timeline__icon" aria-hidden><I size={12} /></span>
                <span className="cw-timeline__t"><span>{e.text}</span><span className="cw-timeline__d">{eventTime(e.at)}</span></span>
              </li>
            );
          })}
        </ol>
      </>}
      {c.pattern && (
        <div className="cw-pattern">
          <span className="cw-pattern__t">{patternLine(c.pattern)}</span>
          <button className="cw-link" onClick={onPattern}>See them on the map<ArrowRight size={14} aria-hidden /></button>
        </div>
      )}
      <div className="cw-side__actions">
        {c.email && first && (
          <a className="cw-btn cw-btn--primary" href={`mailto:${encodeURIComponent(c.email)}?subject=${encodeURIComponent(c.title)}`} onClick={replied}>
            <Reply size={14} aria-hidden />Reply to {first}
          </a>
        )}
        {c.status !== "closed" && <button className="cw-btn" disabled={busy} aria-busy={busy || undefined} onClick={() => void patch({ status: "closed" })}><Check size={14} aria-hidden />Close case</button>}
      </div>
    </aside>
  );
}

function Dialog({ title, onClose, children, wide }: { title: string; onClose: () => void; children: React.ReactNode; wide?: boolean }) {
  const box = useRef<HTMLDivElement>(null);
  useFocusTrap(box, true, { onEscape: onClose });
  return (
    <div className="dialog-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`dialog${wide ? " cw-dialog--wide" : ""}`} role="dialog" aria-modal="true" aria-labelledby="cw-dialog-title" ref={box}>
        <header className="dialog-head">
          <h2 id="cw-dialog-title">{title}</h2>
          <button className="icon-btn" aria-label="Close" onClick={onClose}><X size={16} /></button>
        </header>
        {children}
      </div>
    </div>
  );
}

const CATEGORIES = ["Streets", "Sanitation", "Forestry", "CTA", "Licensing"];

function NewCase({ members, me, onClose, onDone }: { members: Member[]; me: string; onClose: () => void; onDone: (d: CaseDetail) => void }) {
  const [f, setF] = useState({ title: "", category: "", name: "", email: "", phone: "", block: "", srNumber: "", ownerId: me });
  const { busy, run } = useBusy();
  const field = (k: keyof typeof f, label: string, type = "text") => (
    <>
      <label className="field-label" htmlFor={`cw-${k}`}>{label}</label>
      <input id={`cw-${k}`} className="text-input" type={type} value={f[k]} list={k === "category" ? "cw-categories" : undefined}
        onChange={(e) => setF({ ...f, [k]: e.target.value })} />
    </>
  );
  return (
    <Dialog title="New case" onClose={onClose}>
      <form className="cw-form" onSubmit={(e) => { e.preventDefault(); void run(async () => { try { onDone((await call<{ case: CaseDetail }>("/cases", { method: "POST", body: f })).case); } catch (err) { failed(err); } }); }}>
        {field("title", "Case")}
        {field("category", "Category")}
        <datalist id="cw-categories">{CATEGORIES.map((c) => <option key={c} value={c} />)}</datalist>
        {field("name", "Name")}
        {field("email", "Email", "email")}
        {field("phone", "Phone", "tel")}
        {field("block", "Block")}
        {field("srNumber", "311")}
        <label className="field-label" htmlFor="cw-owner">Owner</label>
        <Dropdown id="cw-owner" value={f.ownerId} onChange={(ownerId) => setF({ ...f, ownerId })}
          options={members.map((m) => ({ value: m.id, label: m.name }))} />
        <div className="dialog-actions">
          <button className="cw-btn cw-btn--primary" type="submit" disabled={busy || !f.title.trim()} aria-busy={busy || undefined}><Plus size={14} aria-hidden />New case</button>
        </div>
      </form>
    </Dialog>
  );
}

function ImportDialog({ onClose, onDone }: { onClose: () => void; onDone: (d: CaseDetail) => void }) {
  const [list, setList] = useState<ServiceRequest[] | null>(null);
  const { run } = useBusy();
  useEffect(() => { void call<{ requests: ServiceRequest[] }>("/311").then((d) => setList(d.requests)).catch((e) => { failed(e); setList([]); }); }, []);
  const age = useMemo(() => (iso: string | null) => (iso ? `${Math.max(0, Math.floor((Date.now() - Date.parse(iso)) / 86_400_000))} d` : ""), []);
  return (
    <Dialog title="Import from 311" onClose={onClose} wide>
      <List className="cw-pick">
        {(list ?? []).map((r) => (
          <ListRow key={r.srNumber} className="cw-pick__row"
            onOpen={() => void run(async () => { try { onDone((await call<{ case: CaseDetail }>("/import", { method: "POST", body: { srNumber: r.srNumber } })).case); } catch (e) { failed(e); } })}>
            <RowMain min={120}><RowTitle className="cw-case__t">{r.type}</RowTitle><RowSub className="cw-case__s">{r.block ?? r.address}</RowSub></RowMain>
            <RowTrail><span className="cw-mono">{r.srNumber}</span></RowTrail>
            <RowCell width={64}><span className="cw-c-age">{age(r.createdAt)}</span></RowCell>
          </ListRow>
        ))}
      </List>
    </Dialog>
  );
}

function PatternDialog({ c, onClose }: { c: CaseDetail; onClose: () => void }) {
  const p = c.pattern!;
  return (
    <Dialog title={patternLine(p)} onClose={onClose} wide>
      <List className="cw-pick">
        {p.requests.map((r) => (
          <ListRow key={r.srNumber} className="cw-pick__row">
            <RowMain min={120}><RowTitle className="cw-case__t">{r.address}</RowTitle><RowSub className="cw-case__s">{[r.status, r.createdAt ? eventTime(r.createdAt) : null].filter(Boolean).join(" · ")}</RowSub></RowMain>
            <RowTrail><span className="cw-mono">{r.srNumber}</span></RowTrail>
          </ListRow>
        ))}
      </List>
    </Dialog>
  );
}
